import {
  Copy,
  DotsThreeVertical,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import { pick } from "@styled-system/props";
import { Box, Flex, Price, SystemProps, Text } from "flicket-ui";
import { MouseEvent, ReactNode, useEffect, useState } from "react";
import styled from "styled-components";
import {
  Divider,
  Dropdown,
  DropdownButtonProps,
  DropdownOption,
  Icon,
} from "~components";
import { TicketStatusLabel } from "../constants";
import { ListedReleaseAddon } from "../types";
import { Td, Tr } from "./Table";
import HoverIcon from "./common/HoverIcon";

export const DropdownButton = ({
  isActive,
  setIsOpen,
}: DropdownButtonProps) => {
  return (
    <HoverIcon
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        setIsOpen(!isActive);
      }}
      className={isActive ? "active" : ""}
      color="N600"
      icon={<DotsThreeVertical weight="regular" size={24} />}
    />
  );
};

export function Section({
  children,
  ...props
}: { children: ReactNode } & SystemProps) {
  return (
    <Box {...pick(props)}>
      {children}
      <Divider mb={4} mt={6} />
    </Box>
  );
}

export function Subsection({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box mb={3}>
      <Text mb={1} fontSize={4} fontWeight="heavy" color={"N800"}>
        {title}
      </Text>
      {children}
    </Box>
  );
}

export function SectionTitle({
  title,
  description,
  onClick,
  actionLabel,
  cta,
  small = false,
  ...props
}: {
  title: ReactNode;
  description?: ReactNode;
  onClick?: () => void;
  actionLabel?: string;
  cta?: ReactNode;
  small?: boolean;
} & SystemProps) {
  return (
    <Flex
      mb={2}
      flexDir={{
        _: "column",
        md: "row",
      }}
      justifyContent={"space-between"}
      alignItems={"center"}
      {...pick(props)}
    >
      <Flex flexDir={"column"} pr={actionLabel ? 2 : 0}>
        <Text
          fontSize={small ? 4 : 6}
          pr={actionLabel ? 2 : 0}
          fontWeight="heavy"
          color={"N800"}
        >
          {title}
        </Text>

        {!!description && (
          <Text
            fontSize={3}
            color={"N600"}
            mt={1}
            maxW={cta || actionLabel ? "80%" : "none"}
          >
            {description}
          </Text>
        )}
      </Flex>
      {!cta && actionLabel && (
        <Text
          fontSize={3}
          cursor={"pointer"}
          color={"P300"}
          textDecoration={"underline"}
          onClick={onClick}
        >
          {actionLabel}
        </Text>
      )}
      {!actionLabel && cta && (
        <Flex
          justifyContent={{ _: "flex-start", sm: "flex-end" }}
          flexDir={{ _: "column", sm: "row" }}
          alignItems={{ _: "flex-start", sm: "center" }}
          width={{ _: "100%", sm: "auto" }}
          style={{ gap: 8 }}
          mt={{ _: 2, sm: 0 }}
        >
          {cta}
        </Flex>
      )}
    </Flex>
  );
}

const StyledIcon = styled(Icon)<{ $isVisible?: boolean }>`
  transform: ${(props) =>
    props.$isVisible ? "rotate(90deg)" : "rotate(0deg)"};
  transition: transform 0.1s ease-out;
`;

export function TextCollapse({
  title,
  initialOpen = false,
  hasError = false,
  children,
}: {
  title: string;
  initialOpen?: boolean;
  hasError?: boolean;
  children: ReactNode;
}) {
  const [isVisible, setIsVisible] = useState(initialOpen);
  const [errorState, setErrorState] = useState(false);
  const showChildren = isVisible || errorState;

  useEffect(() => {
    setErrorState(hasError);
  }, [hasError]);
  return (
    <>
      <Box mb={4}>
        <Flex alignItems={"center"} color="P300">
          <StyledIcon
            fontSize={5}
            icon={"chevron-right"}
            $isVisible={showChildren}
          />
          <Text
            textDecoration="underline"
            cursor="pointer"
            fontSize={3}
            onClick={() => !errorState && setIsVisible(!isVisible)}
          >
            <Flex alignItems={"center"}>{title}</Flex>
          </Text>
        </Flex>
      </Box>
      <Box display={showChildren ? "block" : " none"} as="section">
        {children}
      </Box>
    </>
  );
}

export function ListItemWrapper({
  title,
  type = "ticket",
  eventIsActive,
  onClickItem,
  onEdit,
  onCopy,
  onDelete,
  data,
}: {
  type: "addon" | "ticket";
  title: string;
  onEdit?: () => void;
  onCopy?: () => void;
  onDelete?: () => void;
  onClickItem?: () => void;
  data: ListedReleaseAddon;
  eventIsActive: boolean;
}) {
  const dropdownOptions: DropdownOption[] = [];

  if (onEdit) {
    dropdownOptions.push({
      label: "Edit " + type,
      onClick: onEdit,
      icon: <PencilSimple />,
    });
  }

  if (onCopy) {
    dropdownOptions.push({
      label: "Make a copy",
      onClick: onCopy,
      icon: <Copy />,
    });
  }

  if (onDelete) {
    dropdownOptions.push({
      label: "Delete " + type,
      onClick: onDelete,
      icon: <Trash />,
    });
  }

  const label: TicketStatusLabel = (() => {
    if (!data.isActive) return "Hidden";
    if (eventIsActive) return "On sale";
    return "Scheduled";
  })();

  return (
    <Tr onClick={onClickItem}>
      <Td>
        <Box cursor="pointer">
          <Text as="span" mr={2} color="N700" fontSize={3}>
            {title}
          </Text>

          <Text
            as="span"
            fontSize={"13px" as any}
            display={"inline-flex"}
            color={"N500"}
            mr={1}
          >
            {label}
          </Text>
        </Box>
      </Td>
      <Td textAlign="right">
        <Text color="N500" fontSize={2}>
          {data.quantity || "-"}
        </Text>
      </Td>
      <Td textAlign="right" width="80px">
        <Text color="N700" fontSize={3}>
          {data.price ? <Price price={data.price} /> : "Free"}
        </Text>
      </Td>
      <Td textAlign="right" width="1px" pr={{ _: 0, md: 1 }}>
        <Dropdown
          DropdownButton={DropdownButton}
          options={dropdownOptions}
          textAlign="left"
        />
      </Td>
    </Tr>
  );
}

export const FieldLabel = styled(Text).attrs({
  display: "inline-flex",
  fontSize: 3,
  borderRadius: "md",
  backgroundColor: "N100",
  py: 1,
  px: "12px",
  alignItems: "center",
})``;

export const PrimaryText = styled(Text).attrs({
  color: "N800",
  fontSize: 3,
  fontWeight: "medium",
})``;

export const ButtonText = styled(Text).attrs({
  color: "N700",
  display: "inline-flex",
  fontSize: "16px",
  fontWeight: "heavy",
})``;

export const Field = styled.fieldset`
  border: none;
  width: 100%;

  &:disabled {
    .react-select__value-container {
      background-color: ${(p) => p.theme.colors.N100};
    }
  }
`;
